import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MessageCircle, Send, BookOpen, Linkedin, Facebook, Instagram } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_dmtai-ecosystem/artifacts/a7g76rw9_DMTAI%20Leo.png';
const EBOOK_URL = 'https://mailchi.mp/6a067eb22e28/lm-ebook-healthcare-pro';

export default function Footer({ onOpenAudit }) {
  const { t } = useLanguage();

  const contactLinks = [
    { icon: Phone, label: 'WhatsApp', href: 'https://wa.me/+523224750476' },
    { icon: Send, label: 'Telegram', href: 'https://t.me/+523224750476' },
    { icon: Mail, label: 'Email', href: 'mailto:ddarious@proton.me' },
  ];

  const socialLinks = [
    { icon: Linkedin, label: 'LinkedIn', href: 'https://www.linkedin.com/company/dmtech-bc/' },
    { icon: Facebook, label: 'Facebook', href: 'https://facebook.com/dmtech.bc' },
    { icon: Instagram, label: 'Instagram', href: 'https://instagram.com/dmtech.bc' },
  ];

  return (
    <footer className="relative" data-testid="footer">
      {/* Sticky Audit Bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 px-4 py-3 glass-strong border-t border-[var(--border-subtle)]">
        <div className="container-custom flex items-center justify-between">
          <span className="text-sm text-[var(--text-secondary)] hidden sm:block">
            {t('footer.tagline')}
          </span>
          <button
            onClick={onOpenAudit}
            data-testid="footer-audit-btn"
            className="btn-primary text-sm flex items-center gap-2"
          >
            <MessageCircle className="w-4 h-4" />
            {t('footer.audit')}
          </button>
        </div>
      </div>

      {/* Main Footer */}
      <div className="bg-[var(--bg-paper)] border-t border-[var(--border-subtle)] py-12 md:py-16 pb-20">
        <div className="container-custom">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Brand */}
            <div className="space-y-4">
              <Link to="/" className="flex items-center gap-3" data-testid="footer-logo">
                <img 
                  src={LOGO_URL} 
                  alt="DMTAI Logo" 
                  className="h-12 w-12 rounded-full object-cover"
                />
                <div>
                  <span className="block font-bold text-xl gradient-text-gold">DMTAI Strategies</span>
                  <span className="block text-sm text-[var(--text-muted)]">{t('footer.tagline')}</span>
                </div>
              </Link>
              <p className="text-sm text-[var(--text-secondary)] max-w-xs">
                {t('footer.slogan')}
              </p>
              
              {/* Social Media Icons */}
              <div className="flex items-center gap-3 pt-2">
                {socialLinks.map((social, idx) => (
                  <a
                    key={idx}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-social-${social.label.toLowerCase()}`}
                    className="w-9 h-9 rounded-lg bg-[var(--primary-main)] bg-opacity-10 flex items-center justify-center hover:bg-opacity-30 transition-all duration-300 group"
                    title={social.label}
                  >
                    <social.icon className="w-4 h-4 text-[var(--text-secondary)] group-hover:text-[var(--accent-gold)] transition-colors" />
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div className="space-y-4">
              <h4 className="font-bold text-[var(--accent-gold)]">{t('footer.quickLinks')}</h4>
              <ul className="space-y-2">
                <li>
                  <Link to="/" className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors" data-testid="footer-link-home">
                    {t('nav.home')}
                  </Link>
                </li>
                <li>
                  <Link to="/services" className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors" data-testid="footer-link-services">
                    {t('nav.services')}
                  </Link>
                </li>
                <li>
                  <a href="https://calendly.com/dmt-a-master" target="_blank" rel="noopener noreferrer" className="text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors" data-testid="footer-link-consultation">
                    {t('cta.consultation.title')}
                  </a>
                </li>
              </ul>
            </div>

            {/* Contact - Icons Only */}
            <div className="space-y-4">
              <h4 className="font-bold text-[var(--accent-gold)]">{t('footer.contact')}</h4>
              <div className="flex items-center gap-3">
                {contactLinks.map((contact, idx) => (
                  <a
                    key={idx}
                    href={contact.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`footer-contact-${contact.label.toLowerCase()}`}
                    className="w-10 h-10 rounded-lg bg-[var(--primary-main)] bg-opacity-10 flex items-center justify-center hover:bg-opacity-30 transition-all duration-300 group"
                    title={contact.label}
                  >
                    <contact.icon className="w-5 h-5 text-[var(--primary-light)] group-hover:text-[var(--accent-gold)] transition-colors" />
                  </a>
                ))}
              </div>
            </div>

            {/* Free Ebook Lead Magnet */}
            <div className="space-y-4 md:col-span-3 lg:col-span-1">
              <h4 className="font-bold text-[var(--accent-gold)]">{t('footer.freeEbook')}</h4>
              <div className="p-4 rounded-xl glass border border-[var(--border-highlight)] hover:border-[var(--accent-gold)] transition-all duration-300">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-[var(--accent-gold)] bg-opacity-20 flex items-center justify-center flex-shrink-0">
                    <BookOpen className="w-5 h-5 text-[var(--accent-gold)]" />
                  </div>
                  <div>
                    <p className="text-sm text-[var(--text-primary)] font-medium mb-2">
                      {t('footer.ebookTitle')}
                    </p>
                    <a
                      href={EBOOK_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="footer-ebook-cta"
                      className="inline-flex items-center gap-2 text-xs font-semibold text-[var(--accent-gold)] hover:text-[var(--accent-yellow)] transition-colors"
                    >
                      {t('footer.ebookCta')} →
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom */}
          <div className="mt-12 pt-8 border-t border-[var(--border-subtle)] flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-xs text-[var(--text-muted)]">
              © {new Date().getFullYear()} DMTAI Strategies LLC. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              {/* Contact Icons */}
              <a href="https://wa.me/+523224750476" target="_blank" rel="noopener noreferrer" className="text-[var(--text-muted)] hover:text-[var(--primary-light)] transition-colors" title="WhatsApp">
                <Phone className="w-5 h-5" />
              </a>
              <a href="https://t.me/+523224750476" target="_blank" rel="noopener noreferrer" className="text-[var(--text-muted)] hover:text-[var(--primary-light)] transition-colors" title="Telegram">
                <Send className="w-5 h-5" />
              </a>
              <a href="mailto:ddarious@proton.me" className="text-[var(--text-muted)] hover:text-[var(--primary-light)] transition-colors" title="Email">
                <Mail className="w-5 h-5" />
              </a>
              {/* Divider */}
              <span className="w-px h-5 bg-[var(--border-subtle)]"></span>
              {/* Social Icons */}
              <a href="https://www.linkedin.com/company/dmtech-bc/" target="_blank" rel="noopener noreferrer" className="text-[var(--text-muted)] hover:text-[var(--primary-light)] transition-colors" title="LinkedIn">
                <Linkedin className="w-5 h-5" />
              </a>
              <a href="https://facebook.com/dmtech.bc" target="_blank" rel="noopener noreferrer" className="text-[var(--text-muted)] hover:text-[var(--primary-light)] transition-colors" title="Facebook">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://instagram.com/dmtech.bc" target="_blank" rel="noopener noreferrer" className="text-[var(--text-muted)] hover:text-[var(--primary-light)] transition-colors" title="Instagram">
                <Instagram className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
